/**
 * ContactPage
 * One human way in: email info@wildy.world (no form, on purpose), with WhatsApp and phone beside it
 * and who will answer. No response-time promises (see the Playbook rules).
 * Operators who want to be listed are pointed to /for-business.
 */

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Phone, Store } from 'lucide-react';
import { Header } from '@/components/Header';
import { HeroSection } from '@/components/homepage/HeroSection';
import { Footer } from '@/components/homepage/Footer';
import { WhatsAppIcon } from '@/components/shared/WhatsAppIcon';
import { EmailUsCard } from '@/components/shared/EmailUsCard';
import { publicContact } from '@/config/publicContact';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4 },
};

export function ContactPage() {
  const { t } = useTranslation('public', { keyPrefix: 'contact' });
  const { phones } = publicContact;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero: short, so the form is one scroll away */}
      <HeroSection
        hideSearch
        hideFilters
        videoSrc="/video/hero-section-brume-video-background.mp4"
        customContent={
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 sm:pt-[calc(4rem+env(safe-area-inset-top,0px))] text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1
                className="text-[2rem] sm:text-6xl font-bold text-white tracking-tight leading-[1.1] sm:leading-[1.05]"
                style={{ textShadow: '0 4px 20px rgba(0,0,0,0.45)' }}
              >
                <span className="block">{t('hero.title')}</span>
                <span className="block mt-2 bg-gradient-to-r from-action-200 to-action-300 bg-clip-text text-transparent">
                  {t('hero.highlight')}
                </span>
              </h1>
              <p
                className="mt-4 sm:mt-6 mx-auto max-w-2xl text-base sm:text-xl text-white/95 leading-relaxed"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}
              >
                {t('hero.subtitle')}
              </p>
            </motion.div>
          </div>
        }
      />

      <section className="py-16 sm:py-24 bg-brand-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 lg:grid-cols-[3fr_2fr] lg:items-start">
          <motion.div {...fadeUp}>
            <EmailUsCard
              subject={t('email.subject')}
              buttonLabel={t('email.button')}
              copyLabel={t('email.copy')}
              copiedLabel={t('email.copied')}
              note={t('email.note')}
            />
          </motion.div>

          <motion.aside {...fadeUp} transition={{ duration: 0.4, delay: 0.08 }} className="space-y-6">
            {/* Direct channels */}
            <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-brand-900 mb-4">{t('direct.title')}</h2>
              <ul className="space-y-3">
                {phones.map(p => (
                  <li key={p.href}>
                    <a href={p.href} target={p.isWhatsApp ? '_blank' : undefined} rel={p.isWhatsApp ? 'noopener noreferrer' : undefined}
                      className="group flex items-center gap-3 rounded-lg p-2 -m-2 hover:bg-brand-50">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-primary">
                        {p.isWhatsApp ? <WhatsAppIcon size={20} /> : <Phone className="w-5 h-5" aria-hidden />}
                      </span>
                      <span>
                        <span className="block text-sm text-slate-500">
                          {p.isWhatsApp ? t('direct.whatsapp') : t('direct.phone')} · {p.countryCode}
                        </span>
                        <span className="block font-medium text-brand-900 group-hover:underline underline-offset-4">{p.number}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who answers */}
            <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-brand-900">{t('people.title')}</h2>
              <div className="mt-4 flex items-center gap-4">
                <div className="flex -space-x-3 shrink-0">
                  <img src="/images/mario.jpg" alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white" />
                  <img src="/images/guillermo.jpg" alt="" className="h-12 w-12 rounded-full object-cover ring-2 ring-white" />
                </div>
                <p className="text-slate-600 leading-relaxed">{t('people.description')}</p>
              </div>
              <Link to="/about" className="mt-4 inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-4">
                {t('people.link')}
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
            </div>

            {/* Operators */}
            <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-primary">
                <Store className="w-5 h-5" aria-hidden />
              </span>
              <div>
                <p className="text-slate-700 leading-relaxed">
                  <span className="font-semibold text-brand-900">{t('operators.title')}</span>{' '}
                  {t('operators.description')}
                </p>
                <Link to="/for-business" className="mt-2 inline-flex items-center gap-1 font-semibold text-primary hover:underline underline-offset-4">
                  {t('operators.link')}
                  <ArrowRight className="w-4 h-4" aria-hidden />
                </Link>
              </div>
            </div>
          </motion.aside>
        </div>
      </section>

      <Footer />
    </div>
  );
}
